import type { Permission, Role } from '@/shared/api/types';

import { urlOf, type Part } from './site';

/**
 * The interface each role gets. An account has exactly one role, and a role is a
 * job of its own: a student learns and has teachers, a teacher writes courses and
 * has students, an admin moderates, a developer watches the installation run, root
 * manages the accounts. No role includes
 * another: a teacher is nobody's student, an admin does not get the teacher's
 * studio. The role is chosen at registration and is not the account's to change.
 *
 * This only decides what the application shows; the server checks every request.
 */
export const ROLES = ['student', 'teacher', 'admin', 'developer', 'root'] as const;
export type RoleName = (typeof ROLES)[number];

/**
 * The interface a session gets. A guest (public lessons tried without an account,
 * see the server's auth/guests.py) has none of its own: it is shown what a visitor
 * is, and keeps what it does until it signs in.
 */
export const interfaceOf = (role: Role | undefined): RoleName | undefined =>
  role === 'guest' ? undefined : role;

/** A section of the application: an entry of the navigation and the pages under it. */
export type SectionId =
  | 'hub'
  | 'history'
  | 'studio'
  | 'students'
  | 'moderation'
  | 'feedback'
  | 'access'
  | 'glossary'
  | 'help'
  | 'seo'
  | 'tools';

interface RoleInterface {
  /** The part of the installation the role works in (shared/lib/site.ts). */
  part: Part;
  /** Where the account lands after signing in, and where a page that is not its own sends it. */
  home: string;
  /** Its sections, most used first. */
  sections: readonly SectionId[];
  /**
   * Where the sections are listed on a desktop and a tablet: in the bar above the
   * page, or down its side. Those who work in wide tables all day get the side:
   * the bar's width is the tables'. A phone lists them at the bottom either way.
   */
  navigation: 'bar' | 'side';
}

export const INTERFACES: Record<RoleName, RoleInterface> = {
  student: { part: 'app', home: '/', sections: ['hub', 'history', 'glossary', 'help'], navigation: 'bar' },
  teacher: { part: 'app', home: '/studio', sections: ['studio', 'students', 'hub', 'history', 'glossary', 'help'], navigation: 'bar' },
  admin: { part: 'admin', home: '/moderation', sections: ['moderation', 'feedback', 'hub', 'glossary', 'help', 'seo', 'access'], navigation: 'side' },
  developer: { part: 'dev', home: '/portal', sections: ['tools', 'access'], navigation: 'bar' },
  // Root goes everywhere with the one account it has.
  root: { part: 'admin', home: '/access', sections: ['access', 'moderation', 'feedback', 'hub', 'help', 'seo', 'tools'], navigation: 'side' },
};

/**
 * Sections that are part of a role's interface only for the accounts given this
 * permission: an admin or a developer who manages the accounts. Root holds them all.
 */
export const SECTION_PERMISSIONS: Partial<Record<SectionId, Permission>> = {
  access: 'accounts.manage',
  seo: 'seo.manage',
};

/** Whether an account of this role holds the permission. Root holds every one. */
export const holds = (
  role: RoleName | undefined,
  permissions: readonly Permission[] | undefined,
  permission: Permission,
): boolean => role === 'root' || (permissions?.includes(permission) ?? false);

/** Those who may be given the page "SEO", besides root. */
export const SEO_MANAGERS: readonly RoleName[] = ['admin', 'root'];

/** Those who may be given the accounts to manage, besides root. */
export const ACCOUNT_MANAGERS: readonly RoleName[] = ['admin', 'developer', 'root'];

/**
 * Where an account of this role starts: a path when its part is at this address, a
 * whole address when it is on another host. Nobody (a visitor) starts at the front.
 */
export const homeUrl = (role: RoleName | undefined): string =>
  role ? urlOf(INTERFACES[role].part, INTERFACES[role].home) : urlOf('home', '/');

/** What anybody without an account sees: what is public, and what explains it. */
export const VISITOR_SECTIONS: readonly SectionId[] = ['hub', 'glossary', 'help'];

/** Whether the role's sections are listed down the side of the page, not in the bar. */
export const hasSideNavigation = (role: RoleName | undefined): boolean =>
  role !== undefined && INTERFACES[role].navigation === 'side';

/** Those who take lessons and tests, their own or of others: they keep a history of results. */
export const LEARNERS: readonly RoleName[] = ['student', 'teacher'];
/** Those who have teachers: they accept invitations and send results. */
export const STUDENTS: readonly RoleName[] = ['student'];
/** Those who hand users and content over for moderation. */
export const MODERATORS: readonly RoleName[] = ['admin', 'root'];
/** Those who have a part in the product: everybody but a developer, whose work is the installation itself. */
export const MEMBERS: readonly RoleName[] = ['student', 'teacher', 'admin', 'root'];
/** Those who are let into the tools the installation is watched with. */
export const DEVELOPERS: readonly RoleName[] = ['developer', 'root'];
